import { useTheme } from "@emotion/react";
import {
	Autocomplete,
	Button,
	TextField,
	Grid,
	useMediaQuery,
	InputAdornment,
	IconButton,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import debounce from "lodash.debounce";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { renderCodeOption } from "../../utils/functions";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import {
	formatSaveDateFrom,
	formatSaveDateTo,
} from "../../reducers/TimeReducer";
import { Clear } from "@mui/icons-material";
import PATClearButton from "../../components/Buttons/PATClearButton";
import PrintsSearch3 from "../../components/PrintSearch3";

const useStyles = makeStyles((theme) => ({
	sideMenuTitle: {
		fontSize: 20,
		fontWeight: 700,
		color: theme.palette.text.primary,
		marginBottom: "5px",
	},
	colorTitle: {
		color:
			theme.palette.mode === "dark"
				? "#fff !important"
				: "#000 !important",
	},
}));

const defaultStatusOptions = [
	{ label: "All Status", value: "all" },
	{ label: "Open", value: "Open" },
	{ label: "Closed", value: "Closed" },
];

const periodOptions = [
	{ label: "Today", value: "today" },
	{ label: "Last 7 Days", value: "week" },
	{ label: "Last 30 Days", value: "month" },
	{ label: "Last Quarter", value: "quarter" },
	{ label: "Last Year", value: "year" },
];

const headers = [
	{ key: "woNumber", label: "WO#" },
	{ key: "vehicle", label: "Vehicle" },
	{ key: "typeDisplay", label: "Type" },
	{ key: "statusDisplay", label: "Status" },
	{ key: "assignedTo", label: "Assigned To" },
	{ key: "vendor", label: "Vendor" },
	{ key: "openedDateFormatted", label: "Opened" },
	{ key: "closedDateFormatted", label: "Closed" },
	{ key: "downTime", label: "Down Time" },
	{ key: "costFormatted", label: "Cost" },
	{ key: "odometerFormatted", label: "Odometer" },
];

const columnsForPrint = headers.map((h) => ({
	field: h.key,
	headerName: h.label,
	editable: false,
	flex: 1,
	...h,
}));

const WorkOrdersTableToolbar = ({
	onAddClick,
	search,
	setSearch,
	onFilterChange,
	filters,
	resetFilters,
	workOrders,
	vehicleOptions = [],
	vendorOptions = [],
	typeOptions = [],
	employeeOptions = [],
	fundingOptions = [],
	statusOptions = defaultStatusOptions,
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down("md"));
	const btnStyles = {
		color: theme.palette.mode === "dark" ? "#fff" : "primary",
		borderColor: theme.palette.mode === "dark" ? "#fff" : "primary",
		"&:hover": {
			borderColor: theme.palette.mode === "dark" ? "#fff" : "primary",
			backgroundColor: theme.palette.action.hover,
		},
	};

	const [showAdvSearch, setShowAdvSearch] = useState(false);
	const [localFilters, setLocalFilters] = useState(filters);
	const [printFlag, setPrintFlag] = useState(false);
	const [localSearch, setLocalSearch] = useState(search || "");

	useEffect(() => {
		setLocalFilters(filters);
	}, [filters]);

	useEffect(() => {
		setLocalSearch(search || "");
	}, [search]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedSearchChange = useCallback(
		debounce((searchString) => {
			setSearch(searchString);
		}, 500),
		[setSearch],
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFilterChange = useCallback(
		debounce((newFilters) => {
			onFilterChange(newFilters);
		}, 500),
		[onFilterChange],
	);

	const handleSearchChange = (e) => {
		const value = e.target.value;
		setLocalSearch(value);
		debouncedSearchChange(value);
	};

	const handleFilterChange = useCallback(
		(field, value) => {
			let updatedFilters = { ...localFilters, [field]: value };

			// Auto fill From and To based on Period
			if (field === "period" && value?.value) {
				const today = moment().endOf("day");
				let from = null;
				let to = today;

				switch (value.value) {
					case "today":
						from = moment().startOf("day");
						break;

					case "week":
						from = moment().subtract(6, "days").startOf("day");
						break;

					case "month":
						from = moment().subtract(29, "days").startOf("day");
						break;

					case "quarter":
						from = moment().subtract(3, "months").startOf("day");
						break;

					case "year":
						from = moment().subtract(1, "year").startOf("day");
						break;

					default:
						from = null;
						to = null;
				}

				updatedFilters = {
					...updatedFilters,
					from: from ? formatSaveDateFrom(from) : null,
					to: to ? formatSaveDateTo(to) : null,
				};
			}

			setLocalFilters(updatedFilters);
			debouncedFilterChange(updatedFilters);
		},
		[debouncedFilterChange, localFilters],
	);

	const handlePrintClick = () => {
		setPrintFlag(true);
		setTimeout(() => setPrintFlag(false), 1000);
	};

	const renderClearButton = () => (
		<Grid item>
			<PATClearButton title="Clear Filters" onClick={resetFilters} />
		</Grid>
	);

	const renderFilterFields = (fields) =>
		fields.map(
			({ type, label, field, options, xs = 2, multiselect = true }) => (
				<Grid item xs={isSmall ? 12 : xs} key={field}>
					{type === "text" ? (
						<TextField
							label={label}
							value={localFilters[field] || ""}
							onChange={(e) =>
								handleFilterChange(field, e.target.value)
							}
							fullWidth
							variant="outlined"
							size="small"
						/>
					) : type === "date" ? (
						<DatePicker
							label={label}
							value={
								localFilters[field]
									? moment(localFilters[field])
									: null
							}
							onChange={(date) =>
								handleFilterChange(
									field,
									date
										? field === "from"
											? formatSaveDateFrom(date)
											: formatSaveDateTo(date)
										: null,
								)
							}
							minDate={
								field === "to" && localFilters.from
									? moment(localFilters.from)
									: undefined
							}
							maxDate={
								field === "from" && localFilters.to
									? moment(localFilters.to)
									: undefined
							}
							clearable
							clearText="Clear"
							renderInput={(params) => (
								<TextField
									{...params}
									fullWidth
									variant="outlined"
									size="small"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{localFilters[field] && (
													<InputAdornment position="end">
														<IconButton
															onClick={() =>
																handleFilterChange(
																	field,
																	null,
																)
															}
															size="small"
															edge="end"
														>
															<Clear fontSize="small" />
														</IconButton>
													</InputAdornment>
												)}
												{
													params.InputProps
														?.endAdornment
												}
											</>
										),
									}}
								/>
							)}
						/>
					) : (
						<Autocomplete
							multiple={multiselect}
							options={options || []}
							value={
								multiselect
									? localFilters[field] || []
									: localFilters[field] || null
							}
							onChange={(_, value) =>
								handleFilterChange(field, value)
							}
							getOptionLabel={(option) => option?.label || ""}
							renderOption={renderCodeOption}
							isOptionEqualToValue={(o, v) =>
								o?.value === v?.value
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label={label}
									variant="outlined"
									size="small"
								/>
							)}
							fullWidth
							sx={
								multiselect
									? {
											"& .MuiAutocomplete-inputRoot": {
												flexWrap: "nowrap",
												maxHeight: "40px",
											},
											"& .MuiAutocomplete-tag": {
												maxWidth: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											},
											"& .MuiAutocomplete-endAdornment": {
												alignSelf: "flex-start",
											},
										}
									: {}
							}
						/>
					)}
				</Grid>
			),
		);

	// Prepare status options with "All Status" option
	const statusFilterOptions = useMemo(() => {
		const allOption = { label: "All Status", value: "all" };
		const existingOptions = statusOptions.map((s) => ({
			label: s.label || s,
			value: s.value || s,
		}));
		return [allOption, ...existingOptions.filter((s) => s.value !== "all")];
	}, [statusOptions]);

	const filterFields = [
		{
			type: "autocomplete",
			label: "Status",
			field: "status",
			options: statusFilterOptions,
			xs: 1.5,
			multiselect: false,
		},
		{
			type: "autocomplete",
			label: "Type",
			field: "type",
			options: typeOptions,
			xs: 1.5,
			multiselect: true,
		},
	];

	const advanceFilterFields = [
		{
			type: "autocomplete",
			label: "Vehicle",
			field: "vehicleId",
			options: vehicleOptions.map((v) => ({
				label: v.label,
				value: v.value,
			})),
			multiselect: true,
			xs: 2,
		},
		{
			type: "autocomplete",
			label: "Vendor",
			field: "vendor",
			options: vendorOptions,
			multiselect: true,
			xs: 1.5,
		},
		{
			type: "autocomplete",
			label: "Assigned To",
			field: "perPersonnelId",
			options: employeeOptions,
			multiselect: true,
			xs: 2,
		},
		{
			type: "autocomplete",
			label: "Funding",
			field: "fundingSourceId",
			options: fundingOptions,
			multiselect: true,
			xs: 1.5,
		},
	];

	const advancedFilterDateFields = [
		{
			type: "autocomplete",
			label: "Period",
			field: "period",
			options: periodOptions,
			multiselect: false,
			xs: 1.2,
		},
		{
			type: "date",
			label: "From Date",
			field: "from",
			xs: 1.5,
		},
		{
			type: "date",
			label: "To Date",
			field: "to",
			xs: 1.5,
		},
	];

	return (
		<>
			<div>
				<div className={classes.sideMenuTitle}>Work Orders</div>
			</div>
			<div className="section-actions mb-2">
				<TextField
					label="Search WO#, vendor, type..."
					type="search"
					className={classes.filter}
					variant="outlined"
					size="small"
					style={{
						width: "100%",
						maxWidth: 250,
					}}
					value={localSearch}
					onChange={handleSearchChange}
				/>
				{renderFilterFields(filterFields)}
				<Button
					variant="outlined"
					color="primary"
					sx={btnStyles}
					onClick={() => setShowAdvSearch((s) => !s)}
				>
					🔍 Advanced
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={handlePrintClick}
					title="Print current list"
					sx={btnStyles}
				>
					🖨️ Print
				</Button>
				<Button
					variant="contained"
					color="primary"
					onClick={onAddClick}
				>
					+ New Work Order
				</Button>
			</div>
			{showAdvSearch && (
				<div
					style={{
						margin: "8px",
						padding: "14px 16px",
						border: "1px solid var(--border)",
						borderRadius: "8px",
						display: "flex",
						flexWrap: "wrap",
						gap: 8,
						alignItems: "center",
					}}
				>
					<span
						style={{
							fontSize: 11,
							fontWeight: 700,
							textTransform: "uppercase",
							letterSpacing: "0.06em",
							color: "var(--text2)",
							marginRight: 4,
						}}
					>
						Filter by:
					</span>
					{renderFilterFields(advanceFilterFields)}
					<span
						style={{
							fontSize: 11,
							fontWeight: 700,
							textTransform: "uppercase",
							letterSpacing: "0.06em",
							color: "var(--text2)",
							marginRight: 4,
							marginLeft: 8,
						}}
					>
						Opened:
					</span>
					{renderFilterFields(advancedFilterDateFields)}
					{renderClearButton()}
				</div>
			)}

			<PrintsSearch3
				title="Work Orders"
				data={workOrders}
				cols={columnsForPrint}
				print={printFlag}
			/>
		</>
	);
};

export default WorkOrdersTableToolbar;
